/**
 * @class
 * @memberOf p3.camera
 */
export default
class Camera {
    /**
     * @param {PIXI.Point} [view]
     * @param {boolean} [snapToPixel]
     */
    constructor(view, snapToPixel = false) {
        /**
         * @member {Object}
         * @member {signals.Signal} signals.trackStart
         * @member {signals.Signal} signals.trackStop
         */
        this.signals = {
            trackStart: new signals.Signal(),
            trackStop: new signals.Signal()
        };

        /**
         * @member {PIXI.Point}
         */
        this.view = view || new PIXI.Point();

        /**
         * @member {PIXI.Rectangle}
         */
        this.bounds = new PIXI.Rectangle(-(Number.MAX_SAFE_INTEGER * 0.5), -(Number.MAX_SAFE_INTEGER * 0.5),
            Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);

        /**
         * @member {PIXI.Point}
         */
        this.offset = new PIXI.Point();

        /**
         * @member {boolean}
         */
        this.snapToPixel = snapToPixel;

        /**
         * @member {PIXI.Point}
         * @private
         */
        this._position = new PIXI.Point();

        /**
         * @member {*}
         * @private
         */
        this._target = null;

        /**
         * @member {Array.<CameraLayer>}
         * @private
         */
        this._layers = [];

        /**
         * @member {PIXI.Point}
         * @private
         */
        this._ease = new PIXI.Point(0.2, 0.2);

        /**
         * @member {PIXI.Point}
         * @private
         */
        this._parallax = new PIXI.Point(1, 1);

        /**
         * @member {boolean}
         * @private
         */
        this._tracking = false;
    }

    /**
     */
    destroy() {
        this.signals.trackStart.dispose();
        this.signals.trackStop.dispose();

        this.removeAllLayers();
    }

    /**
     * @param {number} dt
     */
    update(dt) {
        const pos = this._target ?
            new PIXI.Point(this._target.x + this.offset.x, this._target.y + this.offset.y) : new PIXI.Point();

        this._clampToBounds(pos);

        if (this._shakeTime > 0) {
            pos.x += this._shakeStrength * P3.math.randomInt(-1, 1) * (this._shakeTime / this._shakeDuration);
            pos.y += this._shakeStrength * P3.math.randomInt(-1, 1) * (this._shakeTime / this._shakeDuration);
            this._shakeTime = Math.max(0, this._shakeTime - dt);
        }

        const dx = pos.x - (this._position.x * this._parallax.x);
        const dy = pos.y - (this._position.y * this._parallax.y);

        this._position.x += dx * (this._ease.x * (1 / this._parallax.x));
        this._position.y += dy * (this._ease.y * (1 / this._parallax.y));

        if (Math.abs(dx) < 0.01) this._position.x = pos.x;
        if (Math.abs(dy) < 0.01) this._position.y = pos.y;

        if (this.snapToPixel) {
            this._position.x = Math.round(this._position.x);
            this._position.y = Math.round(this._position.y);
        }

        const d = (dx * dx + dy * dy);
        if (d < 0.1 && !this._tracking) {
            this._tracking = true;
            this.signals.trackStop.dispatch(this);
        } else if (d > 0.1 && this._tracking) {
            this._tracking = false;
            this.signals.trackStart.dispatch(this);
        }
        this.updateLayers();
    }

    /**
     */
    updateLayers() {
        for (let i = 0; i < this._layers.length; ++i) {
            const layer = this._layers[i];
            layer.view.x = (this._position.x * -layer.parallax.x) + this.view.x - this.view.x * (1 - layer.parallax.x);
            layer.view.y = (this._position.y * -layer.parallax.y) + this.view.y - this.view.y * (1 - layer.parallax.y);
        }
    }

    /**
     * @param {...CameraLayer} layer - The layer(s) to add to the camera.
     * @returns {CameraLayer} The first layer that was added.
     */
    addLayer(layer) {
        const len = arguments.length;
        if (len > 1) {
            for (let i = 0; i < len; ++i) {
                this._layers.push(arguments[i]);
            }
        } else {
            this._layers.push(layer);
        }

        this.updateLayers();
        return layer;
    }

    /**
     * @param {...CameraLayer} layer - The layer(s) to remove from the camera.
     * @returns {CameraLayer} The first layer that was added.
     */
    removeLayer(layer) {
        const len = arguments.length;
        if (len > 1) {
            for (let i = len - 1; i >= 0; --i) {
                this.removeLayer(arguments[i]);
            }
        } else {
            const i = this._layers.indexOf(layer);
            if (i !== -1) {
                this._layers.splice(i, 1);
            } else {
                throw new Error("Layer does not exit!");
            }
        }
        return layer;
    }

    /**
     */
    removeAllLayers() {
        this._layers = [];
    }

    /**
     * @param {*} target
     * @param {boolean} [reset=false]
     */
    setTarget(target, reset = false) {
        this._target = target;

        const layer = this.findLayer(this._target, true);
        this._parallax.x = layer ? layer.parallax.x : 1;
        this._parallax.y = layer ? layer.parallax.y : 1;

        if (reset) {
            const pos = this._target ?
                new PIXI.Point(
                    this._target.x + this.offset.x,
                    this._target.y + this.offset.y
                ) : this._position.clone();

            this._clampToBounds(pos);
            this._position.x = pos.x;
            this._position.y = pos.y;
            this.updateLayers();
        }
    }

    /**
     * @param {number} duration
     * @param {number} strength
     */
    shake(duration, strength) {
        this._shakeTime = this._shakeDuration = duration;
        this._shakeStrength = strength;
    }

    /**
     * @param {PIXI.DisplayObject} displayObject
     * @param {boolean} [recursive=false]
     */
    findLayer(displayObject, recursive = false) {
        let result = null;
        for (let i = 0; i < this._layers.length; ++i) {
            const layer = this._layers[i];
            if (layer.view === displayObject || searchChildren.call(this, layer.view, recursive)) {
                result = layer;
            }
        }
        function searchChildren(displayObject, recursive) {
            for (let i = 0; i < displayObject.children.length; ++i) {
                const child = displayObject.children[i];
                if (child === displayObject) {
                    return true;
                } else if (recursive) {
                    searchChildren.call(this, child, recursive);
                }
            }
            return false;
        }

        return result;
    }

    /**
     * @param position
     * @returns {PIXI.Point}
     * @private
     */
    _clampToBounds(position) {
        if (position.x < this.bounds.x) {
            position.x = this.bounds.x;
        } else if (position.x > this.bounds.width) {
            position.x = this.bounds.width;
        }
        if (position.y < this.bounds.y) {
            position.y = this.bounds.y;
        } else if (position.y > this.bounds.height) {
            position.y = this.bounds.height;
        }
    }

    /**
     * @returns {PIXI.Point}
     */
    get position() {
        return this._position;
    }

    /**
     * @returns {*}
     */
    get target() {
        return this._target;
    }

    /**
     * @returns {PIXI.Point}
     */
    get ease() {
        return this._ease;
    }

    /**
     * @param {PIXI.Point} value
     */
    set ease(value) {
        this._ease = value;
    }

    /**
     * @returns {Array.<CameraLayer>}
     */
    get layers() {
        return this._layers.slice(0);
    }
}